import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const GOLD = '#d4af37';

interface VideoItem {
  id: string;
  title: string;
  videoUrl: string;
  description: string;
  category: string;
  isShort?: boolean;
}

interface VideosContent {
  title: string;
  description: string;
  category: string;
  videos: VideoItem[];
}

const VideosGalleryManager = () => {
  const [content, setContent] = useState<VideosContent>({
    title: 'Temple Videos',
    description: 'Video gallery of temple events and rituals',
    category: 'videos',
    videos: [
      { id: '1', title: 'Aarti Ceremony', videoUrl: 'https://www.youtube.com/embed/example1', description: 'Evening aarti ceremony at temple', category: 'ritual' },
      { id: '2', title: 'Festival Celebrations', videoUrl: 'https://www.youtube.com/embed/example2', description: 'Festival celebration highlights', category: 'festival' },
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'gallery_content', 'videos'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<VideosContent>;
          setContent((prev) => {
            const nextVideos = (data.videos ?? prev.videos).map((video: any) => ({
              ...video,
              category: video.category || 'general'
            }));
            return { ...prev, ...data, videos: nextVideos };
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'gallery_content', 'videos'), content);
      showSnackbar('Videos gallery saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddVideo = () => {
    const newVideo: VideoItem = {
      id: Date.now().toString(),
      title: 'New Video',
      videoUrl: '',
      description: '',
      category: 'general'
    };
    setContent({
      ...content,
      videos: [...content.videos, newVideo]
    });
  };

  // Convert any YouTube URL to embed format
  const toEmbedUrl = (url: string): string => {
    let videoId = '';
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    const shortsMatch = url.match(/\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (watchMatch) videoId = watchMatch[1];
    else if (shortMatch) videoId = shortMatch[1];
    else if (shortsMatch) videoId = shortsMatch[1];
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    return url;
  };

  const handleVideoChange = (id: string, field: keyof VideoItem, value: string) => {
    if (field === 'videoUrl') {
      const isShort = /\/shorts\//.test(value);
      const finalValue = toEmbedUrl(value);
      setContent({
        ...content,
        videos: content.videos.map(video =>
          video.id === id ? { ...video, videoUrl: finalValue, isShort } : video
        )
      });
    } else {
      setContent({
        ...content,
        videos: content.videos.map(video =>
          video.id === id ? { ...video, [field]: value } : video
        )
      });
    }
  };

  const handleDeleteVideo = (id: string) => {
    setContent({
      ...content,
      videos: content.videos.filter(video => video.id !== id)
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Videos Gallery Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage temple video gallery content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🎥 Gallery Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category (Future Filter)"
              value={content.category}
              onChange={(e) => setContent({ ...content, category: e.target.value })}
              size="small"
              placeholder="videos"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            🎬 Videos ({content.videos.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddVideo}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Video
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.videos.map((video) => (
            <Grid item xs={12} key={video.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Video Title"
                      value={video.title}
                      onChange={(e) => handleVideoChange(video.id, 'title', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Box>
                      <TextField
                        fullWidth
                        label="YouTube URL"
                        value={video.videoUrl}
                        onChange={(e) => handleVideoChange(video.id, 'videoUrl', e.target.value)}
                        size="small"
                        placeholder="https://www.youtube.com/watch?v=... or /shorts/..."
                        helperText="Paste any YouTube link — it auto-converts to embed format"
                        sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                      />
                      {video.videoUrl && (
                        <Chip
                          label={video.isShort ? '📱 Short Video' : '🎬 Normal Video'}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            bgcolor: video.isShort ? '#e3f2fd' : '#e8f5e9',
                            color: video.isShort ? '#1565c0' : '#2e7d32'
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteVideo(video.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={video.description}
                      onChange={(e) => handleVideoChange(video.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={video.category || 'general'}
                        onChange={(e) => handleVideoChange(video.id, 'category', e.target.value)}
                      >
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="festival">Festival</MenuItem>
                        <MenuItem value="ritual">Ritual</MenuItem>
                        <MenuItem value="documentary">Documentary</MenuItem>
                        <MenuItem value="tour">Temple Tour</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideosGalleryManager;
